'use client';

import { createContext, useCallback, useContext, useMemo } from 'react';
import { keepPreviousData } from '@tanstack/query-core';
import { parseAsIsoDateTime, useQueryStates } from 'nuqs';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';

type ExternalCallAggregateByCsrOut = ApiSchemas['ExternalCallAggregateByCsrOut'];

export interface CsrFilters {
  dateRange: { from: Date; to: Date };
}

export interface SearchState {
  filters: CsrFilters;
}

export interface CsrAggViewItem {
  csr_name: string;
  total_calls: number;
  total_calls_duration_ms: number;
  avg_call_duration_ms?: number;
  booking_rate: number;
  resolution_rate: number;
  avg_metrics_score?: number;
  rate_metrics_score?: number;
  ring_groups: string[];
  teams: string[];
}

export interface CsrDataContextValue {
  searchState: SearchState;
  setFilters: (filters: Partial<CsrFilters>) => void;
  data: CsrAggViewItem[];
  isLoading: boolean;
}

const CsrDataCtx = createContext<CsrDataContextValue | null>(null);

export function useCsrAggregateDataCtx() {
  const ctx = useContext(CsrDataCtx);
  if (!ctx) {
    throw new Error('useCsrDataCtx must be used within a CsrDataProvider');
  }
  return ctx;
}

function defaultDateRange() {
  const now = new Date();
  // midnight today (local timezone)
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const from = new Date(to);
  from.setDate(to.getDate() - 14);
  return { from, to };
}

export function CsrDataProvider({ children }: { children: React.ReactNode }) {
  const { api } = useApiV1Ctx();
  const { from: defaultFrom, to: defaultTo } = defaultDateRange();
  
  // Use nuqs to manage URL state for filters
  const [filters, setFilters] = useQueryStates(
    {
      from: parseAsIsoDateTime.withDefault(defaultFrom),
      to: parseAsIsoDateTime.withDefault(defaultTo),
    },
    {
      history: 'push', // or 'replace' if you prefer
      shallow: false,
    }
  );

  // Convert nuqs state to our expected format
  const searchState = useMemo<SearchState>(
    () => ({
      filters: {
        dateRange: {
          from: filters.from,
          to: filters.to,
        },
      },
    }),
    [filters.from, filters.to]
  );

  // API query using the filters
  const { data: response, isLoading } = api.externalCalls.getAggregateMetricsExternalCallsMetricsAggregateGet.useQuery(
    {
      query: {
        from_date: searchState.filters.dateRange.from.toISOString(),
        to_date: searchState.filters.dateRange.to.toISOString(),
        group_by: 'user_name',
        rate_metrics: 'booking,resolution,metrics_score',
        avg_metrics: 'duration_ms,metrics_avg_score',
      },
    },
    {
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }
  );

  // Helper to update filters
  const updateFilters = useCallback(
    (newFilters: Partial<CsrFilters>) => {
      const updates: Partial<typeof filters> = {};

      if (newFilters.dateRange?.from !== undefined) {
        updates.from = newFilters.dateRange.from;
      }
      if (newFilters.dateRange?.to !== undefined) {
        updates.to = newFilters.dateRange.to;
      }

      setFilters(updates);
    },
    [setFilters]
  );

  const value = useMemo<CsrDataContextValue>(() => {
    return {
      searchState,
      setFilters: updateFilters,
      data: (response?.data ?? []).map(
        (item: ExternalCallAggregateByCsrOut) =>
          ({
            csr_name: item.grouping_key,
            total_calls: item.total_calls,
            total_calls_duration_ms: item.total_calls_duration_ms,
            avg_call_duration_ms: item.avg_fields?.avg_duration_ms,
            booking_rate: item.rate_fields?.rate_booking ?? 0,
            resolution_rate: item.rate_fields?.rate_resolution ?? 0,
            ring_groups: item.ring_groups ?? [],
            rate_metrics_score: item.rate_fields?.rate_metrics_score ?? 0,
            avg_metrics_score: item.avg_fields?.avg_metrics_avg_score ?? 0,
            teams: item.teams ?? [],
          }) as CsrAggViewItem
      ),
      isLoading,
    };
  }, [searchState, updateFilters, response?.data, isLoading]);

  return <CsrDataCtx.Provider value={value}>{children}</CsrDataCtx.Provider>;
}
